/*
 * SuppliersDashboard.tsx
 */

// Imports React

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AddButton, DeleteButton, MainButton } from '../common';
import {
	TiersData,
	deleteTiers,
	TiersTypeData,
	tiersApi,
} from 'features/tiers';
import { ordersApi } from '../features/orders/services/ordersApi';
import { OrderData } from '../features/orders/types/types';
import * as Progress from '@radix-ui/react-progress';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faAngleRight,
	faBox,
	faIndustry,
	faPen,
	faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import { ContractsCrudCard } from 'features/contracts/components/ContractsCrudCard';
import { ExpensesCrudCard } from 'features/expenses/components/ExpensesCrudCard';
import { SupplierInvoicesCrudCard } from 'features/supplierInvoices/components/SupplierInvoicesCrudCard';

// SuppliersDashboard page component
const SuppliersDashboard: React.FC = () => {
	const isCustomerView = false;
	const isSupplierView = true;

	// State management
	const [loading, setLoading] = useState(true);
	const [tiers, setTiers] = useState<TiersData[]>([]);
	const navigate = useNavigate();
	const [orders, setOrders] = useState<OrderData[]>([]);

	// Fetch tiers on component mount
	useEffect(() => {
		// Get token from localStorage
		const token = localStorage.getItem('token');
		// If no token, exit
		if (!token) return;
		// Fetch tiers from API

		tiersApi
			.listPaginated(token, {
				sort: 'label',
				order: 'ASC',
				limit: 999999,
			})
			.then((res) => {
				setTiers(
					res.data.filter((tier) => {
						const isClient = tier.types?.some((type) => type.code === 'CLI');
						const isSupplier = tier.types?.some((type) => type.code === 'FOU');
						if (isCustomerView && !isSupplierView) {
							return isClient;
						} else if (!isCustomerView && isSupplierView) {
							return isSupplier;
						}
						return true;
					})
				);
			})
			.catch(console.error)
			.finally(() => setLoading(false));
	}, []);

	useEffect(() => {
		// Get token from localStorage
		const token = localStorage.getItem('token');
		// If no token, exit
		if (!token) return;
		// Fetch orders from API
		ordersApi
			.listPaginated(token, { limit: 999999 })
			.then((res) => setOrders(res.data))
			.finally(() => setLoading(false));
	}, []);

	// Handlers for add, edit, and delete actions
	const handleAdd = () => {
		if (isCustomerView) {
			navigate('/app/tiers/new?roles=client');
		} else if (isSupplierView) {
			navigate('/app/tiers/new?roles=fournisseur');
		}
	};
	const handleManage = (id: number) => navigate(`/app/tiers/${id}/manage`);
	const handleDelete = (id: number) => {
		const token = localStorage.getItem('token');
		if (!token) return;

		deleteTiers(id, token)
			.then(() => {
				setTiers((prev) => prev.filter((tiers) => tiers.id !== id));
			})
			.catch((error) => console.error(error));
	};

	const isClient = (t: TiersTypeData[]): boolean => {
		return t?.some((type) => type.code === 'CLI');
	};

	const isSupplier = (t: TiersTypeData[]): boolean => {
		return t?.some((type) => type.code === 'FOU');
	};

	const handleAddOrder = () => {
		navigate('/app/orders/new');
	};

	const handleManageOrder = (id: number) =>
		navigate(`/app/orders/${id}/manage`);

	const handleDeleteOrder = (id: number) => {
		const token = localStorage.getItem('token');
		if (!token) return;

		ordersApi
			.delete(id, token)
			.then(() => {
				setOrders((prev) => prev.filter((order) => order.id !== id));
			})
			.catch((error) => console.error(error));
	};

	// Rendu du composant SuppliersDashboard
	return (
		<div>
			{/* Grille haute : 2 colonnes (1/2 et 2/2) */}
			<div className="grid gap-6 grid-cols-1 md:grid-cols-[1fr_1fr] mb-6">
				{/* CARD Contenant les informations des fournisseurs */}
				<div className=" p-6 bg-white rounded-lg shadow-md w-full">
					{/* HEADER DE LA CARD */}
					<div className="flex mb-3 text-gray-700 items-center">
						<FontAwesomeIcon icon={faIndustry} className="w-6 h-6 mr-2" />
						<h2 className="text-2xl font-bold">Fournisseurs</h2>
						<div className="ml-auto">
							<AddButton label="" onClick={handleAdd} size="sm" />
						</div>
					</div>
					{loading ? (
						isCustomerView ? (
							<p>Chargement des clients...</p>
						) : (
							<p>Chargement des fournisseurs...</p>
						)
					) : (
						<div className="overflow-x-auto">
							{/* Table structure with headers and rows for each tiers */}
							<table className="min-w-full border border-gray-200 rounded-lg">
								{/* Table headers */}
								<thead className="bg-gray-50">
									<tr>
										<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
											Label
										</th>
										<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
											Actions
										</th>
									</tr>
								</thead>
								{/* Table body */}
								<tbody className="bg-white divide-y divide-gray-200">
									{/* tiers.map((tier) et filtre sur isCustomerView et isSupplierView selon tier.type (fonction isClient et isSupplier)*/}
									{tiers
										.filter((tier) => {
											if (isCustomerView && !isSupplierView) {
												return isClient(tier.types ?? []);
											} else if (!isCustomerView && isSupplierView) {
												return isSupplier(tier.types ?? []);
											}
											return true;
										})
										.map((tier) => (
											<tr key={tier.id}>
												<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
													{tier.label}
												</td>
												<td className="px-6 py-4 border-b space-x-2">
													<div className="flex space-x-2">
														{handleManage && (
															<MainButton
																label=""
																onClick={() => handleManage(tier.id)}
																size="sm"
																icon={faPen}
															/>
														)}
														{handleDelete && (
															<DeleteButton
																label=""
																question={`Voulez-vous vraiment supprimer le tiers ${tier.label} ?`}
																onConfirm={() => handleDelete(tier.id)}
																size="sm"
																icon={faTrashAlt}
															/>
														)}
													</div>
												</td>
											</tr>
										))}
								</tbody>
							</table>
						</div>
					)}
				</div>

				{/* CARD Contenant les informations des contrats */}
				<ContractsCrudCard
					size="sm"
					returnPath="/app/contracts"
					returnLabel="Tous les contrats"
					returnIcon={faAngleRight}
					returnInvert={true}
					className="w-full"
				/>
			</div>

			{/* Grille basse : 2 colonnes fusionnées*/}
			<div className="grid gap-6 grid-cols-1 md:grid-cols-2 mb-6">
				{/* CARD Contenant les informations des commandes */}
				<div className=" p-6 bg-white rounded-lg shadow-md col-span-2">
					{/* HEADER DE LA CARD */}
					<div className="flex mb-3 text-gray-700 items-center">
						<FontAwesomeIcon icon={faBox} className="w-6 h-6 mr-2" />
						<h2 className="text-2xl font-bold">Commandes</h2>
						<div className="ml-auto">
							<AddButton label="" onClick={handleAddOrder} size="sm" />
						</div>
					</div>

					{loading ? (
						<p>Chargement des commandes...</p>
					) : (
						<div className="overflow-x-auto">
							{/* Table structure with headers and rows for each order */}
							<table className="min-w-full border border-gray-200 rounded-lg">
								{/* Table headers */}
								<thead className="bg-gray-50">
									<tr>
										<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
											Référence
										</th>
										<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
											Client
										</th>
										<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
											Montant
										</th>
										<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
											Statut
										</th>
										<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
											Actions
										</th>
									</tr>
								</thead>
								{/* Table body */}
								<tbody className="bg-white divide-y divide-gray-200">
									{Array.isArray(orders) &&
										orders.map((order) => {
											const lastVersion =
												order.orderVersions?.[order.orderVersions.length - 1];
											const lines = lastVersion?.orderLines || [];

											const percent =
												lines.length > 0
													? lines.reduce(
															(acc, line) => acc + (line.deliveredPercent || 0),
															0
														) / lines.length
													: 0;

											const isComplete = percent >= 100;

											return (
												<tr key={order.id}>
													<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
														{order.referenceNumber}
													</td>
													<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
														{order.tiers?.label}
													</td>
													<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
														{(
															order.orderVersions?.[
																order.orderVersions.length - 1
															]?.orderLines?.reduce(
																(acc, line) =>
																	acc +
																	(line.totalExclTax ? line.totalExclTax : 0),
																0
															) ?? 0
														).toLocaleString('fr-FR', {
															minimumFractionDigits: 2,
															maximumFractionDigits: 2,
														})}{' '}
														€
													</td>

													<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
														<Progress.Root className="w-full h-2 bg-gray-200 rounded-full">
															<Progress.Indicator
																className={`h-full rounded-full ${
																	isComplete ? 'bg-green-700' : 'bg-blue-600'
																}`}
																style={{
																	width: `${percent}%`,
																	transform: 'none',
																}}
															/>
														</Progress.Root>
													</td>
													<td className="px-6 py-4 border-b space-x-2">
														<div className="flex space-x-2">
															{handleManageOrder && (
																<MainButton
																	label=""
																	onClick={() => handleManageOrder(order.id)}
																	size="sm"
																	icon={faPen}
																/>
															)}
															{handleDeleteOrder && (
																<DeleteButton
																	label=""
																	question={`Voulez-vous vraiment supprimer la commande ${order.id} ?`}
																	onConfirm={() => handleDeleteOrder(order.id)}
																	size="sm"
																	icon={faTrashAlt}
																/>
															)}
														</div>
													</td>
												</tr>
											);
										})}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>

			{/* Grille basse : 2 colonnes*/}
			<div className="grid gap-6 grid-cols-1 md:grid-cols-2 mb-6">
				{/* CARD Contenant les informations des dépenses */}
				<ExpensesCrudCard
					size="sm"
					returnPath="/app/expenses"
					returnLabel="Toutes les dépenses"
					returnIcon={faAngleRight}
					returnInvert={true}
					className="w-full"
				/>
				{/* CARD Contenant les informations des factures */}
				<SupplierInvoicesCrudCard
					size="sm"
					returnPath="/app/supplierInvoices"
					returnLabel="Toutes les factures"
					returnIcon={faAngleRight}
					returnInvert={true}
					className="w-full"
				/>
			</div>
		</div>
	);
};

export default SuppliersDashboard;
